/**
 * TherapyStyleSelector Component
 *
 * Reusable component for selecting a therapy style.
 * Supports both full card view (for settings) and compact dropdown (for forms).
 * Displays style name and description with visual selection indicator.
 */

import { useTherapyStyles } from "../hooks/useTherapyStyles";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Label } from "./ui/label";
import { Loader2 } from "lucide-react";

interface TherapyStyleSelectorProps {
  value: string | null;
  onChange: (value: string | null) => void;
  variant?: "cards" | "compact";
  disabled?: boolean;
  showUseDefault?: boolean;
}

export function TherapyStyleSelector({
  value,
  onChange,
  variant = "cards",
  disabled = false,
  showUseDefault = false,
}: TherapyStyleSelectorProps) {
  const { data: styles, isLoading, error } = useTherapyStyles();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">
          Loading therapy styles...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 p-4">
        <p className="text-sm text-red-600 dark:text-red-400">
          Failed to load therapy styles. Please try again.
        </p>
      </div>
    );
  }

  if (!styles?.length) {
    return (
      <div className="rounded-lg border border-border bg-muted p-4">
        <p className="text-sm text-muted-foreground">
          No therapy styles available.
        </p>
      </div>
    );
  }

  if (variant === "compact") {
    return (
      <Select
        value={value || "default"}
        onValueChange={(v) => onChange(v === "default" ? null : v)}
        disabled={disabled}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select a therapy style" />
        </SelectTrigger>
        <SelectContent>
          {showUseDefault && (
            <SelectItem value="default">Use account default</SelectItem>
          )}
          {styles.map((style) => (
            <SelectItem key={style.id} value={style.id}>
              {style.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <RadioGroup
      value={value || ""}
      onValueChange={(v) => onChange(v || null)}
      disabled={disabled}
      className="space-y-3"
    >
      {styles.map((style) => (
        <label
          key={style.id}
          className={`flex cursor-pointer items-start gap-4 rounded-lg border p-4 transition-colors ${
            value === style.id
              ? "border-primary bg-primary/5"
              : "border-border hover:border-border"
          } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
        >
          <RadioGroupItem value={style.id} id={style.id} className="mt-1" />
          <div className="flex-1">
            <Label
              htmlFor={style.id}
              className="cursor-pointer text-base font-medium text-foreground"
            >
              {style.name}
            </Label>
            <p className="mt-1 text-sm text-muted-foreground">
              {style.description}
            </p>
          </div>
        </label>
      ))}
    </RadioGroup>
  );
}
